import React, { useId } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'next-i18next/pages';

import {
  Button,
  Collapse,
  Flex,
  Paper,
  Space,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from '@highlight-cp/core-ui';

export interface BatchOperationItem {
  id: string;
  index: number;
  outcome: string;
  message: string;
}

export interface BatchOperationErrorProps {
  message: string;
  items: BatchOperationItem[];
}

const DEFAULT_SUPPORT_LINK = {
  title: '#epd-support',
  href: 'https://letshighlight.slack.com/archives/C014L098M8X',
};

export const DefaultSupportLink = () => (
  <a href={DEFAULT_SUPPORT_LINK.href} rel="noreferrer" target="_blank">
    {DEFAULT_SUPPORT_LINK.title}
  </a>
);

export const BatchOperationError: React.FC<BatchOperationErrorProps> = ({ message, items }) => {
  const { t } = useTranslation();
  const [opened, { toggle }] = useDisclosure(false);
  const detailsId = useId();

  const messageComponent = (
    <Text size="sm" c="var(--mantine-color-error)" role="alert" aria-live="polite" aria-atomic>
      {message}
    </Text>
  );

  if (items.length === 0) {
    return messageComponent;
  }

  return (
    <Paper radius="md" bg="var(--mantine-color-red-light)" p="xs">
      <Flex justify="space-between" align="center">
        {messageComponent}
        <Button
          color="red"
          size="xs"
          onClick={toggle}
          aria-expanded={opened}
          aria-controls={detailsId}
        >
          {t('Details')}
        </Button>
      </Flex>
      <Collapse id={detailsId} expanded={opened}>
        <Space h="md" />
        <Text>
          Please review and/or see{' '}
          <a href={DEFAULT_SUPPORT_LINK.href} rel="noreferrer" target="_blank">
            {DEFAULT_SUPPORT_LINK.title}
          </a>{' '}
          for assistance!
        </Text>
        <Space h="md" />
        <Table fz="sm">
          <TableHeader>
            <TableRow>
              <TableHead>{t('#')}</TableHead>
              <TableHead>{t('ID')}</TableHead>
              <TableHead>{t('Outcome')}</TableHead>
              <TableHead>{t('Message')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={`${item.index}-${item.id}`}>
                <TableCell>{item.index}</TableCell>
                <TableCell>{item.id}</TableCell>
                <TableCell>{item.outcome}</TableCell>
                <TableCell>{item.message}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Collapse>
    </Paper>
  );
};
